import { useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, Loader2, CreditCard } from "lucide-react";
import { RetellWebClient } from "retell-client-js-sdk";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type CallState = "idle" | "connecting" | "active";

/**
 * Start/stop a live voice call with the Retell agent (natural voice).
 * The backend creates the web call; the Retell SDK streams audio in-browser.
 * If the tenant's automatic usage recharge failed (card declined), the server
 * returns 402 PAYMENT_METHOD_FAILED and we show a blocking payment notice.
 */
export default function RetellCallButton() {
  const { toast } = useToast();
  const [callState, setCallState] = useState<CallState>("idle");
  const [showPaymentBlocked, setShowPaymentBlocked] = useState(false);
  const clientRef = useRef<RetellWebClient | null>(null);

  useEffect(() => {
    return () => {
      clientRef.current?.stopCall();
      clientRef.current = null;
    };
  }, []);

  const startCall = async () => {
    setCallState("connecting");
    try {
      const res = await apiRequest("POST", "/api/retell/web-call", {});
      const { accessToken } = (await res.json()) as { accessToken: string };

      const client = new RetellWebClient();
      clientRef.current = client;
      client.on("call_started", () => setCallState("active"));
      client.on("call_ended", () => {
        setCallState("idle");
        clientRef.current = null;
      });
      client.on("error", (err: unknown) => {
        console.error("Retell call error:", err);
        toast({ title: "Call error", description: "The voice call ended unexpectedly.", variant: "destructive" });
        client.stopCall();
        clientRef.current = null;
        setCallState("idle");
      });

      await client.startCall({ accessToken });
    } catch (err) {
      setCallState("idle");
      clientRef.current = null;
      const message = err instanceof Error ? err.message : "Could not start the voice call.";
      if (message.includes("PAYMENT_METHOD_FAILED") || message.includes("automatic usage charge")) {
        setShowPaymentBlocked(true);
      } else {
        toast({ title: "Call failed", description: message, variant: "destructive" });
      }
    }
  };

  const endCall = () => {
    clientRef.current?.stopCall();
    clientRef.current = null;
    setCallState("idle");
  };

  return (
    <>
      <Dialog open={showPaymentBlocked} onOpenChange={setShowPaymentBlocked}>
        <DialogContent data-testid="dialog-payment-blocked">
          <div className="flex min-h-full flex-col items-center justify-center gap-6 p-8 text-center">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-destructive/10">
              <CreditCard className="h-12 w-12 text-destructive" />
            </div>
            <DialogHeader className="items-center">
              <DialogTitle className="text-3xl font-bold text-destructive">
                Calls Paused — Payment Needed
              </DialogTitle>
              <DialogDescription className="max-w-xl text-lg">
                Your account reached its usage limit and the automatic recharge to your card could
                not be processed. Voice calls cannot be made or received until the payment method
                is updated and the charge goes through.
              </DialogDescription>
            </DialogHeader>
            <p className="max-w-xl text-base text-muted-foreground">
              Please update your payment method in Billing. Calls resume automatically as soon as
              the outstanding balance is charged successfully.
            </p>
            <Button size="lg" onClick={() => setShowPaymentBlocked(false)} data-testid="button-payment-blocked-close">
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {callState === "active" ? (
        <Button
          variant="destructive"
          size="sm"
          className="h-8 gap-1.5"
          onClick={endCall}
          data-testid="button-retell-end-call"
        >
          <PhoneOff className="h-4 w-4" /> End call
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          onClick={startCall}
          disabled={callState === "connecting"}
          data-testid="button-retell-call"
        >
          {callState === "connecting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
          {callState === "connecting" ? "Connecting…" : "Voice call"}
        </Button>
      )}
    </>
  );
}
